<template>
  <div class="dropdown">
    <button
      class="btn btn-outline-secondary dropdown-toggle w-100"
      type="button"
      data-bs-toggle="dropdown"
      aria-expanded="false"
    >
      Repositories ({{ selectedRepositories.length }})
    </button>
    <ul class="dropdown-menu w-100 repository-dropdown-menu">
      <li class="px-3 py-2" @click.stop>
        <input
          type="text"
          class="form-control form-control-sm"
          placeholder="Search repositories..."
          v-model="searchQuery"
        />
      </li>
      <li><hr class="dropdown-divider" /></li>
      <div class="repository-list-container">
        <li
          v-for="group in filteredRepositoryGroups"
          :key="'group-' + group.id"
          class="dropdown-header d-flex align-items-center"
          v-on:click.stop="toggleGroup(group)"
        >
          <div class="form-check flex-grow-1">
            <input
              class="form-check-input"
              type="checkbox"
              :id="'checkbox-group-' + group.id"
              :checked="isGroupSelected(group)"
            />
            <div class="form-check-label w-100" :for="'checkbox-group-' + group.id">
              {{ group.name }}
            </div>
          </div>
        </li>
        <li
          v-for="repo in filteredRepositoriesWithoutGroup"
          :key="'repo-' + repo.id"
          v-on:click.stop="toggleRepository(repo)"
          :value="repo.id"
          :id="'repo-' + repo.id"
        >
          <div class="dropdown-item d-flex align-items-center">
            <div class="form-check flex-grow-1">
              <input
                class="form-check-input"
                type="checkbox"
                :id="'checkbox-repo-' + repo.id"
                :checked="selectedRepositories.includes(repo.id.toString())"
              />
              <div class="form-check-label w-100" :for="'repo-' + repo.id">
                {{ repo.title }}
              </div>
            </div>
          </div>
        </li>
        <template v-for="group in filteredRepositoryGroups" :key="'group-repos-' + group.id">
          <li
            v-for="repo in group.repositories"
            :key="'repo-' + repo.id"
            v-on:click.stop="toggleRepository(repo)"
            :value="repo.id"
            :id="'repo-' + repo.id"
          >
            <div class="dropdown-item d-flex align-items-center">
              <div class="form-check flex-grow-1">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="'checkbox-repo-' + repo.id"
                  :checked="selectedRepositories.includes(repo.id.toString())"
                />
                <div class="form-check-label w-100" :for="'repo-' + repo.id">
                  {{ repo.title }}
                </div>
              </div>
            </div>
          </li>
        </template>
        <li
          v-if="filteredRepositoryGroups.length === 0 && filteredRepositoriesWithoutGroup.length === 0"
          class="dropdown-item text-muted"
        >
          No repositories found
        </li>
      </div>
    </ul>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from "vue";

export default defineComponent({
  name: "RepositorySelector",
  props: {
    repositories: {
      type: Array,
      required: true
    },
    initialSelected: {
      type: Array,
      default: () => []
    }
  },
  emits: ["update:selected"],
  setup(props, { emit }) {
    const selectedRepositories = ref(props.initialSelected);
    const searchQuery = ref("");

    const repositoryGroups = computed(() => {
      const groups = {};
      props.repositories.forEach((repo) => {
        if (repo.repository_group) {
          if (!groups[repo.repository_group.id]) {
            groups[repo.repository_group.id] = {
              id: repo.repository_group.id,
              name: repo.repository_group.name,
              repositories: []
            };
          }
          groups[repo.repository_group.id].repositories.push(repo);
        }
      });
      return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
    });

    const repositoriesWithoutGroup = computed(() => {
      return props.repositories.filter((repo) => !repo.repository_group);
    });

    const filteredRepositoryGroups = computed(() => {
      if (!searchQuery.value.trim()) {
        return repositoryGroups.value;
      }

      const query = searchQuery.value.toLowerCase();
      return repositoryGroups.value
        .map((group) => ({
          ...group,
          repositories: group.repositories.filter((repo) => repo.title.toLowerCase().includes(query))
        }))
        .filter((group) => group.repositories.length > 0 || group.name.toLowerCase().includes(query));
    });

    const filteredRepositoriesWithoutGroup = computed(() => {
      if (!searchQuery.value.trim()) {
        return repositoriesWithoutGroup.value;
      }

      const query = searchQuery.value.toLowerCase();
      return repositoriesWithoutGroup.value.filter((repo) => repo.title.toLowerCase().includes(query));
    });

    function repoKey(repo) {
      return repo.id.toString();
    }

    function isGroupSelected(group) {
      return group.repositories.every((repo) => selectedRepositories.value.includes(repoKey(repo)));
    }

    function toggleRepository(repo) {
      const key = repoKey(repo);
      const index = selectedRepositories.value.indexOf(key);
      if (index === -1) {
        selectedRepositories.value.push(key);
      } else {
        selectedRepositories.value.splice(index, 1);
      }
      emit("update:selected", selectedRepositories.value);
    }

    function toggleGroup(group) {
      const groupRepos = group.repositories;
      const allSelected = groupRepos.every((repo) => selectedRepositories.value.includes(repoKey(repo)));

      if (allSelected) {
        groupRepos.forEach((repo) => {
          const index = selectedRepositories.value.indexOf(repoKey(repo));
          if (index !== -1) {
            selectedRepositories.value.splice(index, 1);
          }
        });
      } else {
        groupRepos.forEach((repo) => {
          if (!selectedRepositories.value.includes(repoKey(repo))) {
            selectedRepositories.value.push(repoKey(repo));
          }
        });
      }
      emit("update:selected", selectedRepositories.value);
    }

    return {
      selectedRepositories,
      searchQuery,
      repositoryGroups,
      repositoriesWithoutGroup,
      filteredRepositoryGroups,
      filteredRepositoriesWithoutGroup,
      repoKey,
      isGroupSelected,
      toggleRepository,
      toggleGroup
    };
  }
});
</script>

<style scoped>
.dropdown-item {
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.repository-dropdown-menu {
  max-height: 500px;
}

.repository-list-container {
  max-height: 400px;
  overflow-y: auto;
}
</style>
